import { useContext, useEffect, useState } from 'react';
import enviro from 'enviro';
import { fetchCsatSurvey } from '../api/surveyApi';
import SurveyConfigContext from '../context/SurveyConfigContext';
import { SURVEY_CONFIG_STATUS, FEEDBACK_TEST_MODE_DEBUG_FLAG } from '../constants/surveyConstants';
const hsq = window._hsq = window._hsq || [];
export const isTestModeEnabled = () => {
  return enviro.debug(FEEDBACK_TEST_MODE_DEBUG_FLAG);
};
const getSurveyPortalId = () => {
  const isQa = enviro.isQa();
  return isQa ? 100765651 : 53;
};
export const getFeedbackIgnoreKey = (surveyId, surveyType) => `FEEDBACK_IGNORED&${surveyType}&${surveyId}&${getSurveyPortalId()}`;
export const hideSurvey = (feedbackIgnoreKey, days = 14) => {
  if (isTestModeEnabled()) {
    return;
  }
  const expiry = new Date(Date.now() + days * 24 * 60 * 60 * 1000);
  try {
    localStorage.setItem(feedbackIgnoreKey, expiry.toISOString());
  } catch (error) {
    return;
  }
};
export const isSurveyHidden = (surveyId, surveyType) => {
  if (isTestModeEnabled()) {
    return false;
  }
  const feedbackIgnoreKey = getFeedbackIgnoreKey(surveyId, surveyType);
  let item;
  try {
    item = localStorage.getItem(feedbackIgnoreKey);
  } catch (error) {
    return false;
  }
  if (!item) return false;
  if (new Date(item) > new Date()) {
    return true;
  }
  try {
    localStorage.removeItem(feedbackIgnoreKey);
  } catch (error) {
    return false;
  }
  return false;
};

/**
 * Load analytics js file for tracking feedback surveyed count
 * https://git.hubteam.com/HubSpot/feedback-web-renderer-ui/blob/0d683253e391cc3361748f7ce765d5d934799202/feedback-loader/static/js/index.js#L52-L57
 */
const loadAnalyticsJS = () => {
  if (isTestModeEnabled()) {
    return;
  }
  if (document.getElementById('analytics-js')) {
    return;
  }
  const isQa = enviro.isQa();
  const qaSuffix = isQa ? 'qa' : '';
  const analyticsSrc = `//js.hubspot${qaSuffix}.com/analytics/0/common.js`;
  const script = document.createElement('script');
  script.setAttribute('id', 'analytics-js');
  script.setAttribute('async', 'true');
  script.setAttribute('src', analyticsSrc);
  document.body.appendChild(script);
};
export const useHideSurvey = surveyConfig => {
  useEffect(() => {
    if (surveyConfig && surveyConfig.config) {
      const {
        config
      } = surveyConfig;
      const key = getFeedbackIgnoreKey(config.surveyId, config.surveyType);
      hideSurvey(key);
    }
  }, [surveyConfig]);
};
export const useSurveyConfig = (surveyId, surveyType) => {
  const [surveyConfig, setSurveyConfig] = useState({
    status: SURVEY_CONFIG_STATUS.NOT_FETCHED
  });
  const surveyConfigContext = useContext(SurveyConfigContext);
  if (isTestModeEnabled()) {
    console.log('[TEST MODE] Returning test survey config:', {
      status: surveyConfig === null || surveyConfig === void 0 ? void 0 : surveyConfig.status,
      surveyId,
      surveyType
    });
  }
  useEffect(() => {
    if (isSurveyHidden(surveyId, surveyType)) {
      setSurveyConfig({
        status: SURVEY_CONFIG_STATUS.FETCHED
      });
      return;
    }
    if (surveyConfigContext && surveyConfigContext.config) {
      setSurveyConfig(surveyConfigContext);
    } else {
      setSurveyConfig({
        status: SURVEY_CONFIG_STATUS.FETCHING
      });
      // Voiding so we don't have to handle the error in this function
      // The error is already caught in `fetchCsatSurvey`
      void fetchCsatSurvey(surveyType, surveyId).then(response => {
        setSurveyConfig(Object.assign({}, response, {
          status: SURVEY_CONFIG_STATUS.FETCHED
        }));
      });
    }
  }, [surveyConfigContext, surveyId, surveyType]);
  return [surveyConfig, setSurveyConfig];
};
export const useTrackSurveyView = surveyConfig => {
  useEffect(() => {
    if (isTestModeEnabled() && surveyConfig && surveyConfig.config) {
      const {
        surveyId,
        surveyType
      } = surveyConfig.config;
      console.log('[TEST MODE] Tracking survey view:', {
        surveyId,
        surveyType
      });
      return;
    }
    if (surveyConfig && surveyConfig.config) {
      const {
        surveyId,
        surveyType
      } = surveyConfig.config;
      loadAnalyticsJS();
      hsq.push(['trackFeedbackView', {
        surveyId,
        surveyType
      }]);
    }
  }, [surveyConfig]);
};